import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroPencilSquare, heroXMark } from '@ng-icons/heroicons/outline';
import { finalize, switchMap } from 'rxjs';
import { MESSAGES } from 'src/app/core/constant/messages';
import { InputSelectComponent } from 'src/app/shared/component/input-select/input-select.component';
import { InputTextComponent } from 'src/app/shared/component/input-text/input-text.component';
import { InputTextareaComponent } from 'src/app/shared/component/input-textarea/input-textarea.component';
import { LoadingButtonComponent } from 'src/app/shared/component/loading-button/loading-button.component';
import { BaseFormComponent } from 'src/app/shared/form/base-form.component';
import { Profile } from 'src/app/shared/model/user';
import { ToastService } from 'src/app/shared/service/toast.service';
import { TokenStorageService } from 'src/app/shared/service/token-storage.service';
import {
  DepartamentService,
  DistrictService,
  ProvinceService,
  UserService,
} from 'src/app/api/services';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { LoadingComponent } from 'src/app/shared/component/loading/loading.component';

@Component({
  selector: 'app-profile',
  imports: [
    CommonModule,
    LoadingButtonComponent,
    InputTextComponent,
    NgIcon,
    FormsModule,
    ReactiveFormsModule,
    InputTextareaComponent,
    RouterModule,
    InputSelectComponent,
    LoadingComponent,
  ],
  templateUrl: './profile.component.html',
  viewProviders: [provideIcons({ heroPencilSquare, heroXMark })],
})
export class ProfileComponent extends BaseFormComponent implements OnInit {
  profile!: Profile;

  isLoading: boolean = false;
  isEditingPresentation: boolean = false;
  loadingSavePresentation: boolean = false;
  isEditingPersonalInfo: boolean = false;
  loadingSavePersonalInfo: boolean = false;

  presentationForm: FormGroup;
  personalInfoForm: FormGroup;

  departaments!: any[];
  districts!: any[];
  provinces!: any[];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private usuarioService: UserService,
    private departamentService: DepartamentService,
    private provinceService: ProvinceService,
    private districtService: DistrictService,
    private tokenStorage: TokenStorageService,
    toastService: ToastService
  ) {
    super(toastService);
    this.presentationForm = this.fb.group({
      personalInformation: [
        '',
        [Validators.required, Validators.minLength(10), Validators.maxLength(500)],
      ],
    });

    this.personalInfoForm = this.fb.group({
      idDepartament: ['', Validators.required],
      idProvince: ['', Validators.required],
      idDistrict: ['', Validators.required],
      address: ['', Validators.required],
      identificationDocument: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(8),
          Validators.pattern(/^[0-9]+$/),
        ],
      ],
      telfOne: ['', [Validators.maxLength(20)]],
    });
  }

  ngOnInit(): void {
    this.loadProfile();
    this.setupCascadingSelects();
    this.loadDepartaments();
  }

  private loadProfile(): void {
    const authData = this.tokenStorage.getAuth();
    if (!authData?.user?.idUser) return;

    this.isLoading = true;
    this.usuarioService
      .getProfile(authData.user.idUser)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (data) => this.populateProfile(data.dto.user),
        error: (err) => this.showApiError(err, MESSAGES.USER.UPDATE_ERROR),
      });
  }

  private populateProfile(user: Profile): void {
    this.profile = user;

    this.personalInfoForm.patchValue({ 
      idDepartament: user.idDepartament || '',
      address: user.address || '',
      identificationDocument: user.identificationDocument || '',
      telfOne: user.telfOne || '',
    });

    if (user.idDepartament) {
      this.loadProvinces(user.idDepartament).then(() => {
        this.personalInfoForm.patchValue({ idProvince: user.idProvince || '' });

        if (user.idProvince) {
          this.loadDistricts(user.idProvince).then(() => {
            this.personalInfoForm.patchValue({
              idDistrict: user.idDistrict || '',
            });
          });
        }
      });
    }
  }

  private loadDepartaments(): void {
    this.departamentService.departamentoGet().subscribe({
      next: (data) => {
        this.departaments = data.dto.departaments.map((item: any) => ({
          value: item.idDepartament,
          label: item.name,
        }));
      },
      error: (err) =>
        this.showApiError(err, MESSAGES.DEPARTAMENT.GET_ALL_ERROR),
    });
  }

  toggleEditPresentation() {
    this.isEditingPresentation = !this.isEditingPresentation;
    if (this.isEditingPresentation) {
      this.presentationForm.patchValue({
        personalInformation: this.profile.personalInformation || '',
      });
    }
  }

  savePresentation(): void {
    if (!this.validateForm(this.presentationForm)) return;

    const newInfo = this.presentationForm.value.personalInformation;
    const authData = this.tokenStorage.getAuth();
    if (!authData?.user?.idUser) return;

    this.loadingSavePresentation = true;

    this.usuarioService
      .updatePersonalInformation(newInfo)
      .pipe(
        switchMap(() => this.usuarioService.getProfile(authData.user!.idUser)),
        finalize(() => (this.loadingSavePresentation = false))
      )
      .subscribe({
        next: (data) => {
          this.profile = data.dto.user;
          this.presentationForm.patchValue({
            personalInformation: this.profile.personalInformation || '',
          });
          this.isEditingPresentation = false;
          this.toastService.showMessages('success', [
            MESSAGES.USER.UPDATE_SUCCESS,
          ]);
        },
        error: (err) => this.showApiError(err, MESSAGES.USER.UPDATE_ERROR),
      });
  }

  toggleEditPersonalInfo() {
    this.isEditingPersonalInfo = !this.isEditingPersonalInfo;
    if (this.isEditingPersonalInfo) {
      this.personalInfoForm.patchValue({
        identificationDocument: this.profile.identificationDocument || '',
        telfOne: this.profile.telfOne || '',
        address: this.profile.address || '',
      });
    }
  }

  savePersonalInfo(): void {
    if (!this.validateForm(this.personalInfoForm)) return;

    const authData = this.tokenStorage.getAuth();
    if (!authData?.user?.idUser) return;

    const formValue = this.personalInfoForm.value;

    this.loadingSavePersonalInfo = true;

    this.usuarioService
      .updateProfile({
        idDistrict: formValue.idDistrict ?? null,
        identificationDocument: formValue.identificationDocument ?? null,
        telfOne: formValue.telfOne ?? null,
        address: formValue.address ?? null,
        birthDate: this.profile.birthDate,
      })
      .pipe(
        switchMap(() => this.usuarioService.getProfile(authData.user!.idUser)),
        finalize(() => (this.loadingSavePersonalInfo = false))
      )
      .subscribe({
        next: (data) => {
          this.profile = data.dto.user;
          this.personalInfoForm.patchValue({
            idDepartament: this.profile.idDepartament || '',
            idProvince: this.profile.idProvince || '',
            idDistrict: this.profile.idDistrict || '',
            address: this.profile.address || '',
            telfOne: this.profile.telfOne || '',
            identificationDocument: this.profile.identificationDocument || '',
          });

          this.isEditingPersonalInfo = false;
          this.toastService.showMessages('success', [
            MESSAGES.USER.UPDATE_SUCCESS,
          ]);
        },
        error: (err) => this.showApiError(err, MESSAGES.USER.UPDATE_ERROR),
      });
  }

  setupCascadingSelects() {
    this.personalInfoForm
      .get('idDepartament')
      ?.valueChanges.subscribe((idDepartament) => {
        this.personalInfoForm.patchValue(
          { idProvince: '', idDistrict: '' },
          { emitEvent: false }
        );
        this.provinces = [];
        this.districts = [];

        if (idDepartament) this.loadProvinces(idDepartament);
      });

    this.personalInfoForm
      .get('idProvince')
      ?.valueChanges.subscribe((idProvince) => {
        this.personalInfoForm.patchValue(
          { idDistrict: '' },
          { emitEvent: false }
        );
        this.districts = [];

        if (idProvince) this.loadDistricts(idProvince);
      });
  }

  loadProvinces(idDepartament: string): Promise<void> {
    return new Promise((resolve) => {
      this.provinceService
        .provinciaDepartamentoIdDepartamentGet({ idDepartament })
        .subscribe({
          next: (res) => {
            this.provinces = res?.dto?.provinces.map((item: any) => ({
              value: item.id,
              label: item.name,
            }));
            resolve();
          },
          error: () => resolve(),
        });
    });
  }

  loadDistricts(idProvince: string): Promise<void> {
    return new Promise((resolve) => {
      this.districtService
        .distritoProvinciaIdProvinceGet({ idProvince })
        .subscribe({
          next: (res) => {
            this.districts = res?.dto?.districts.map((item: any) => ({
              value: item.idDistrict,
              label: item.name,
            }));
            resolve();
          },
          error: () => resolve(),
        });
    });
  }

  goToCompany() {
    this.router.navigate(['/user/company']);
  }

  goToHome() {
    this.router.navigate(['/']);
  }
}
